import { pickT } from "@repo/shared";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { T } from "./code-entry-form.i18n";

const RESEND_COOLDOWN_SECONDS = 60;

export type CodeEntryFormProps = {
  channel: "email" | "sms";
  onVerify: (code: string) => void;
  onResend: () => void | Promise<void>;
  verifying: boolean;
  resending: boolean;
  error: string | null;
  success: string | null;
  /** The flow / link language the calling page shows its own text in. */
  language: string;
};

/**
 * QL-505 — the 6-digit verification-code entry step, shared by the group/client
 * link's `/p/:token/auth` and a member's `/p/m/:token`. Owns the code input and
 * the resend cooldown timer so both callers get identical behaviour; sending
 * the code (choosing e-mail/SMS) and verifying it against the server stay with
 * the caller, since the two pages call different endpoints. Its words are
 * shown in `language`, the flow / link language the caller passes (these pages
 * have no language scope).
 */
export function CodeEntryForm({
  channel,
  onVerify,
  onResend,
  verifying,
  resending,
  error,
  success,
  language,
}: CodeEntryFormProps) {
  const t = pickT(T, language);
  const [code, setCode] = useState("");
  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN_SECONDS);
  const canResend = resendTimer <= 0;

  useEffect(() => {
    if (resendTimer <= 0) return;
    const timer = setTimeout(() => setResendTimer((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code) return;
    onVerify(code);
  };

  const handleResend = async () => {
    await onResend();
    setResendTimer(RESEND_COOLDOWN_SECONDS);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert variant="success">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="code">
          {t.verificationCode} <span className="text-destructive">*</span>
        </Label>
        <Input
          id="code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="123456"
          required
          maxLength={6}
          className="text-center text-2xl tracking-widest font-mono"
          disabled={verifying}
          autoComplete="off"
        />
        <p className="text-xs text-muted-foreground">
          {t.enterCodeFrom} {channel === "sms" ? t.sms : t.email}
        </p>
      </div>

      <Button type="submit" className="w-full" disabled={!code} loading={verifying}>
        {t.verifyContinue}
      </Button>

      <div className="text-center text-sm text-muted-foreground mt-4">
        <p>{t.codeExpires}</p>
        <p className="mt-2">
          {t.didntReceive}{" "}
          <button
            type="button"
            className="text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleResend}
            disabled={!canResend || resending}
          >
            {!canResend ? t.resendIn(String(resendTimer)) : t.resendCode}
          </button>
        </p>
      </div>
    </form>
  );
}
